/* eslint-disable @typescript-eslint/no-var-requires */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { VideoClient } from '@zoom/videosdk';
import SentryService from '../sentry/Sentry.service';
import { useLazyQuery, useMutation } from '@apollo/client';
import { JOIN_VIDEO_SESSION } from '@/shared/queries';
import {
  MutationEndVideoSessionArgs,
  MutationResult,
  MutationStartVideoSessionArgs,
  QueryJoinVideoSessionArgs,
  QueryResult,
} from '@/shared/types/generated';
import { END_VIDEO_SESSION, START_VIDEO_SESSION } from '@/shared/mutations';
import { useGetUser } from '@/shared/hooks/useGetUser';
import useStore from '@/stores/useStore';
import {
  USE_ZOOM_UI_TOOLKIT,
  USE_TWILIO_VIDEO,
} from '@/shared/constants/flags';
import { ZoomWebUIToolkit } from '@/shared/types/video';
import { formatNameWithLastInitial } from '@/shared/utils';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useShallow } from 'zustand/react/shallow';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';
const defaultUIToolkitConfig = {
  videoSDKJWT: '',
  sessionName: 'SessionA',
  userName: 'UserA',
  disableCaptionsOnJoin: true,
  features: ['video', 'audio'],
  options: { init: {}, audio: {}, video: {}, share: {} },
  virtualBackground: {
    allowVirtualBackground: true,
    allowVirtualBackgroundUpload: true,
    virtualBackgrounds: [
      'https://images.unsplash.com/photo-1715490187538-30a365fa05bd?q=80&w=1945&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    ],
  },
};

interface ZoomContextType {
  zoomClient: typeof VideoClient | undefined;
  startSession: (guestId: number, consultationId: number) => Promise<void>;
  joinSession: (sessionName: string) => Promise<void>;
  endSession: (sessionName?: string) => Promise<void>;
  sessionStartTime?: number;
  leaveSession: () => Promise<void>;
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

export const ZoomProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useGetUser();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { toggleInstantStyling } = useInstantStylingToggle();

  const {
    activeSessionName,
    setActiveSessionName,
    setIsInSession,
    setCompletedSessionDuration,
    setSessionChannelUrl,
    setEstimatedSessionDuration,
    setCompletedConsultationId,
  } = useStore(
    useShallow((state) => ({
      activeSessionName: state.activeSessionName,
      setActiveSessionName: state.setActiveSessionName,
      setIsInSession: state.setIsInSession,
      setCompletedSessionDuration: state.setCompletedSessionDuration,
      setSessionChannelUrl: state.setSessionChannelUrl,
      setEstimatedSessionDuration: state.setEstimatedSessionDuration,
      setCompletedConsultationId: state.setCompletedConsultationId,
    })),
  );

  const { isInstantStylingActive, resetClientAndSharedState } =
    useInstantStylingStore(
      useShallow((state) => ({
        isInstantStylingActive: state.isInstantStylingActive,
        resetClientAndSharedState: state.resetClientAndSharedState,
      })),
    );

  const [zoomClient, setZoomClient] = useState<typeof VideoClient>();
  const [UIToolkit, setUIToolkit] = useState<ZoomWebUIToolkit | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number>();

  const [startVideoSession] = useMutation<
    MutationResult<'startVideoSession'>,
    MutationStartVideoSessionArgs
  >(START_VIDEO_SESSION);

  const [joinVideoSession] = useLazyQuery<
    QueryResult<'joinVideoSession'>,
    QueryJoinVideoSessionArgs
  >(JOIN_VIDEO_SESSION);

  const [endVideoSession] = useMutation<
    MutationResult<'endVideoSession'>,
    MutationEndVideoSessionArgs
  >(END_VIDEO_SESSION);

  // Import ZoomVideoSDK for web dynamically
  useEffect(() => {
    if (USE_TWILIO_VIDEO || !window?.navigator) {
      return;
    }

    try {
      const ZoomVideoSDK = require('@zoom/videosdk');
      const client = ZoomVideoSDK.default.createClient();
      setZoomClient(client);
    } catch (error) {
      SentryService.captureError('Failed to load Zoom SDK', error);
    }
  }, []);

  useEffect(() => {
    const initZoom = async () => {
      if (USE_TWILIO_VIDEO || !zoomClient) {
        return;
      }

      await zoomClient.init('en-US', 'CDN', {
        patchJsMedia: true,
        stayAwake: true,
        leaveOnPageUnload: false,
        enforceMultipleVideos: true,
        enforceVirtualBackground: true,
      });
    };

    initZoom();
  }, [zoomClient]);

  // Load Zoom UI Toolkit for web dynamically
  useEffect(() => {
    if (USE_TWILIO_VIDEO) {
      return;
    }

    const loadToolkit = () => {
      if (window.WebZoomUIToolkit) {
        __DEV__ && console.log('Zoom UI Toolkit already loaded.');
        setUIToolkit(window.WebZoomUIToolkit);
        return;
      }

      // Inject Zoom CSS from CDN
      if (!document.getElementById('zoom-ui-css')) {
        const link = document.createElement('link');
        link.id = 'zoom-ui-css';
        link.rel = 'stylesheet';
        link.href =
          'https://cdn.jsdelivr.net/npm/@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
        document.head.appendChild(link);
      }

      // Inject Zoom UI Toolkit script from CDN
      const script = document.createElement('script');
      script.src =
        'https://cdn.jsdelivr.net/npm/@zoom/videosdk-ui-toolkit/index.js';
      script.type = 'module';
      script.async = true;

      script.onload = async () => {
        __DEV__ && console.log('Zoom UI Toolkit script loaded.');

        try {
          // Extract module from script tag
          const module = await window.eval(`import("${script.src}")`);
          __DEV__ && console.log('Zoom UI Toolkit module:', module);

          // Store the module globally for future use
          window.WebZoomUIToolkit = module.default || module;
          setUIToolkit(window.WebZoomUIToolkit);
        } catch (error) {
          SentryService.captureError('Failed to load Zoom UI Toolkit', error);
        }
      };

      script.onerror = () => {
        SentryService.captureError('Failed to load Zoom UI Toolkit script.');
      };

      document.head.appendChild(script);
    };

    USE_ZOOM_UI_TOOLKIT && loadToolkit();
  }, []);

  const attachUIToolkitClosedListener = (
    toolkit: ZoomWebUIToolkit,
    sessionName: string,
  ) => {
    if (typeof toolkit.onSessionClosed === 'function') {
      toolkit.onSessionClosed(async () => {
        try {
          await endSession(sessionName);
        } catch (error) {
          SentryService.captureError('Failed to end video session', error);
        } finally {
          setActiveSessionName('');
          setIsInSession(false);
        }
      });
    } else {
      __DEV__ && console.warn('onSessionClosed event not available.');
    }
  };

  const joinSessionWithUIToolkit = async (
    sessionName: string,
    token: string,
  ) => {
    if (!UIToolkit) {
      throw new Error('Zoom UI Toolkit not initialized');
    }

    const config = {
      ...defaultUIToolkitConfig,
      videoSDKJWT: token,
      sessionName,
      userName: user?.firstName ?? 'Guest',
    };

    const sessionContainer = document.getElementById('video-player-container');
    UIToolkit.joinSession(sessionContainer, config);
    attachUIToolkitClosedListener(UIToolkit, sessionName);
  };

  const startSession = async (guestId: number, consultationId: number) => {
    if (USE_TWILIO_VIDEO) {
      return;
    }

    if (!zoomClient) {
      throw new Error('Zoom client not initialized');
    }

    const { data } = await startVideoSession({
      variables: {
        input: {
          guestId,
          consultationId,
        },
      },
    });

    const sessionName = data?.startVideoSession?.session.sessionName;
    const token = data?.startVideoSession?.zoomToken;
    const startedAt = data?.startVideoSession?.session.startedAt;

    if (!sessionName || !token) {
      throw new Error('Session name or token not found');
    }

    setActiveSessionName(sessionName);
    setSessionStartTime(startedAt);

    if (isInstantStylingActive) {
      await toggleInstantStyling();
    }

    __DEV__ && console.log('Joining Zoom session:', sessionName);

    if (USE_ZOOM_UI_TOOLKIT) {
      joinSessionWithUIToolkit(sessionName, token);
      return;
    } else {
      const userName =
        user?.lastName && user.lastName.length > 0
          ? formatNameWithLastInitial(user)
          : (user?.firstName ?? 'Host');
      await zoomClient.join(sessionName, token, userName);
    }
  };

  const joinSession = async (sessionName: string) => {
    if (!zoomClient) {
      throw new Error('Zoom client not initialized');
    }

    const { data } = await joinVideoSession({
      variables: {
        input: {
          sessionName,
        },
      },
    });

    const token = data?.joinVideoSession?.zoomToken;
    const startedAt = data?.joinVideoSession?.session.startedAt;

    if (!token) {
      throw new Error('Zoom token not found');
    }

    setActiveSessionName(sessionName);
    setSessionStartTime(startedAt);

    if (isInstantStylingActive) {
      await toggleInstantStyling();
    }

    __DEV__ && console.log('Joining Zoom session:', sessionName);

    if (USE_ZOOM_UI_TOOLKIT) {
      joinSessionWithUIToolkit(sessionName, token);
      return;
    } else {
      const userName =
        user?.lastName && user.lastName.length > 0
          ? formatNameWithLastInitial(user)
          : (user?.firstName ?? 'Guest');
      await zoomClient.join(sessionName, token, userName);
    }
  };

  const leaveSession = async () => {
    if (USE_TWILIO_VIDEO) {
      return;
    }

    if (!zoomClient) {
      throw new Error('Zoom client not initialized');
    }
    zoomClient.leave(true);
  };

  const endSession = async (sessionName?: string) => {
    if (USE_TWILIO_VIDEO) {
      return;
    }

    if (!zoomClient) {
      throw new Error('Zoom client not initialized');
    }

    const { data: sessionData } = await endVideoSession({
      variables: {
        input: {
          sessionName: sessionName || activeSessionName,
        },
      },
    });

    const duration = sessionData?.endVideoSession?.durationSeconds;
    setCompletedSessionDuration(duration);

    __DEV__ && console.log('Ending Zoom session:', activeSessionName, duration);

    if (!USE_ZOOM_UI_TOOLKIT) {
      // Always end zoom session since only stylist (host) can be on web
      zoomClient.leave(true);
    }

    const consultation = sessionData?.endVideoSession?.session.consultation;
    const estimatedDuration = consultation?.estimatedDuration;
    const hostId = sessionData?.endVideoSession?.session.hostId;
    const guestId = sessionData?.endVideoSession?.session.guestId;

    setEstimatedSessionDuration(estimatedDuration);
    setCompletedConsultationId(consultation?.id);

    try {
      if (hostId && guestId) {
        const channelUrl = await fetchChannelUrl(hostId, guestId);
        setSessionChannelUrl(channelUrl);
      }
    } catch (error) {
      SentryService.captureError('Failed to fetch channel url', error);
    }

    // Payment capture is now handled automatically by the backend
    // based on attendance and session duration

    resetClientAndSharedState();
  };

  return (
    <ZoomContext.Provider
      value={{
        zoomClient,
        startSession,
        joinSession,
        endSession,
        sessionStartTime,
        leaveSession,
      }}
    >
      {children}
    </ZoomContext.Provider>
  );
};

export const useZoom = (): ZoomContextType => {
  const context = useContext(ZoomContext);
  if (!context) {
    throw new Error('useZoom must be used within a ZoomProvider');
  }
  return context;
};
